/* =================================================================
   Zentrales Datenmodell der Mehrwertanalyse (Source of Truth).
   Pflicht/Optional gem. Brief §42.
   ================================================================= */

export type JaNein = "ja" | "nein" | "";
export type JaNeinWeiss = "ja" | "nein" | "weiss_nicht" | "";

export type AdvisorId = "nick" | "aaron" | "simon" | "unbekannt" | "";

export interface VorsorgeVertrag {
  id: string;
  vertragsart: string; // Pflicht
  anbieter: string; // Pflicht (oder "weiss nicht")
  anbieterUnbekannt: boolean;
  beitrag: string; // Pflicht (oder "weiss nicht")
  beitragUnbekannt: boolean;
  produkt?: string;
  beginn?: string;
  ende?: string;
  hochrechnung?: string;
  vertragsnummer?: string;
}

export interface AkVertrag {
  id: string;
  vertragsart: string; // Berufsunfähigkeit / Grundfähigkeit / Unfall / sonstige
  anbieter: string;
  anbieterUnbekannt: boolean;
  beitrag: string;
  beitragUnbekannt: boolean;
  leistung: string;
  leistungUnbekannt: boolean;
  beginn?: string;
  ende?: string;
  vertragsnummer?: string;
}

export interface SachVertrag {
  id: string;
  art: string; // entspricht einer gewählten Versicherungsart
  anbieter: string;
  anbieterUnbekannt: boolean;
  beitrag: string;
  intervall: "monatlich" | "jaehrlich" | "";
  beginn?: string;
  ende?: string;
  leistung?: string;
  vertragsnummer?: string;
}

export interface VermoegensPosten {
  id: string;
  kategorie: string;
  wert: string;
  wertUnbekannt: boolean;
  sparbeitrag?: string;
  anbieter?: string;
  notiz?: string;
}

export type UploadStatus = "pending" | "uploading" | "done" | "error";
export interface UploadMeta {
  id: string;
  kategorie: string;
  originalName: string;
  safeName: string;
  mime: string;
  size: number;
  status: UploadStatus;
  progress: number;
  driveFileId?: string;
  uploadedAt?: string;
}

export interface Analyse {
  meta: { vorgangsnummer: string; startedAt: string; consentAt: string };

  ziele: {
    beruehrungspunkte: string[];
    wichtig: string[];
    ziele: string[];
    zieleFreitext: string;
  };

  stammdaten: {
    vorname: string;
    nachname: string;
    geburtsdatum: string;
    geburtsort: string;
    staatsangehoerigkeit: string;
    strasse: string;
    hausnummer: string;
    plz: string;
    ort: string;
    email: string;
    mobil: string;
    familienstand: string;
    hatKinder: JaNein;
    anzahlKinder: string;
    alterKinder: string;
    berufsstatus: string;
    berufsbezeichnung: string;
    abschluss: string;
  };

  gesundheit: {
    koerperlich: JaNein;
    psychisch: JaNein;
    raucher: JaNein;
    risikohobby: JaNein;
    risikohobbyText: string;
  };

  altersvorsorge: { vorhanden: JaNeinWeiss; vertraege: VorsorgeVertrag[] };
  arbeitskraft: { vorhanden: JaNeinWeiss; vertraege: AkVertrag[] };
  sachversicherungen: { auswahl: string[]; vertraege: SachVertrag[] };
  vermoegen: { vorhanden: JaNeinWeiss; posten: VermoegensPosten[] };

  strom: {
    eigenerVertrag: JaNeinWeiss;
    anbieter: string;
    abschlag: string;
    vertragsende: string;
    kuendigungsfrist: string;
  };
  gas: {
    eigenerVertrag: JaNeinWeiss;
    anbieter: string;
    abschlag: string;
    vertragsende: string;
    kuendigungsfrist: string;
  };

  krankenversicherung: {
    gesetzlichVersichert: JaNein; // Gate: gesetzlich vs. privat/familienversichert
    anbieter: string; // Pflicht, wenn gesetzlich
    bonusprogramme: JaNein;
    bonusprogrammeText: string;
    halt: string; // Pflicht, wenn gesetzlich – Bindung an aktuelle Kasse
  };

  einkommen: {
    bruttoMonat: string; // nur Info – NICHT summiert
    nettoMonat: string;
    gehaltszahlungen: string; // "12" | "13" | "14" | "andere"
    gehaltszahlungenAndere: string;
    nebeneinkuenfte: string;
    kindergeld: string;
    weitereEinnahmen: string;
  };
  einkommensnachweis: {
    art: "jahresendabrechnung" | "gehaltsabrechnung" | "keine" | "";
    begruendung: string;
  };

  ausgaben: {
    wohnen: string;
    lebenshaltung: string;
    mobilitaet: string;
    kreditraten: string;
    unterhalt: string;
    versicherungen: string;
    versZielText: string; // Pflicht, wenn Versicherungsbeiträge > Summe der oben erfassten Verträge
    abosVereine: string;
    sonstige: string;
    sparbeitraege: string;
    sparZielText: string; // Pflicht, wenn Sparbeiträge > Summe der oben erfassten Sparprodukte
  };

  zukunft: { mindestens: string; komfortabel: string; einmalig: string };

  verantwortung: "selbst" | "unterstuetzung" | "abgeben" | "";
  risiko: number; // 1..10
  flexibilitaet: number; // 1..10
  praeferenz:
    | "kurzfristige_verluste"
    | "geringe_ertraege"
    | "verfuegbarkeit"
    | "falsche_entscheidung"
    | "";

  dokumente: UploadMeta[];
  sonstiges: string;

  beraterId: AdvisorId;

  einwilligungen: {
    datenschutz: boolean;
    verarbeitung: boolean;
    uebermittlung: boolean;
    weiterleitung: boolean;
    driveSpeicherung: boolean;
    gesundheit: boolean;
    angabenGeprueft: boolean;
  };
}

export function createEmptyAnalyse(): Analyse {
  return {
    meta: { vorgangsnummer: "", startedAt: new Date().toISOString(), consentAt: "" },
    ziele: { beruehrungspunkte: [], wichtig: [], ziele: [], zieleFreitext: "" },
    stammdaten: {
      vorname: "", nachname: "", geburtsdatum: "", geburtsort: "",
      staatsangehoerigkeit: "", strasse: "", hausnummer: "", plz: "", ort: "",
      email: "", mobil: "", familienstand: "", hatKinder: "", anzahlKinder: "",
      alterKinder: "", berufsstatus: "", berufsbezeichnung: "", abschluss: "",
    },
    gesundheit: { koerperlich: "", psychisch: "", raucher: "", risikohobby: "", risikohobbyText: "" },
    altersvorsorge: { vorhanden: "", vertraege: [] },
    arbeitskraft: { vorhanden: "", vertraege: [] },
    sachversicherungen: { auswahl: [], vertraege: [] },
    vermoegen: { vorhanden: "", posten: [] },
    strom: { eigenerVertrag: "", anbieter: "", abschlag: "", vertragsende: "", kuendigungsfrist: "" },
    gas: { eigenerVertrag: "", anbieter: "", abschlag: "", vertragsende: "", kuendigungsfrist: "" },
    krankenversicherung: { gesetzlichVersichert: "", anbieter: "", bonusprogramme: "", bonusprogrammeText: "", halt: "" },
    einkommen: {
      bruttoMonat: "", nettoMonat: "", gehaltszahlungen: "12", gehaltszahlungenAndere: "",
      nebeneinkuenfte: "", kindergeld: "", weitereEinnahmen: "",
    },
    einkommensnachweis: { art: "", begruendung: "" },
    ausgaben: {
      wohnen: "", lebenshaltung: "", mobilitaet: "", kreditraten: "", unterhalt: "",
      versicherungen: "", versZielText: "", abosVereine: "", sonstige: "", sparbeitraege: "", sparZielText: "",
    },
    zukunft: { mindestens: "", komfortabel: "", einmalig: "" },
    verantwortung: "",
    risiko: 5,
    flexibilitaet: 5,
    praeferenz: "",
    dokumente: [],
    sonstiges: "",
    beraterId: "",
    einwilligungen: {
      datenschutz: false, verarbeitung: false, uebermittlung: false,
      weiterleitung: false, driveSpeicherung: false, gesundheit: false, angabenGeprueft: false,
    },
  };
}

/* ---------------- Optionslisten ---------------- */

export const OPT_BERUEHRUNGSPUNKTE = [
  "Keine Vorkenntnisse", "Versicherungen", "Altersvorsorge", "Investments",
  "Förderungen", "Immobilien", "Bereits beraten worden", "Sonstiges",
];

export const OPT_WICHTIG = [
  "Verständliche Erklärungen", "Transparenz", "Flexibilität", "Sicherheit",
  "Renditechancen", "Persönliche Betreuung", "Digitale Abwicklung",
  "Geringe Kosten", "Langfristige Begleitung",
];

export const OPT_ZIELE = [
  "Finanzielle Sicherheit", "Vermögen aufbauen", "Altersvorsorge verbessern",
  "Förderungen nutzen", "Versicherungen optimieren", "Steuern sparen",
  "Immobilie finanzieren", "Familie absichern", "Flexibler werden", "Sonstiges",
];

export const OPT_FAMILIENSTAND = [
  "ledig", "verheiratet", "eingetragene Lebenspartnerschaft",
  "geschieden", "verwitwet", "getrennt lebend",
];

/** Der eine Status, bei dem keine Berufsbezeichnung verlangt werden kann. */
export const OHNE_TAETIGKEIT = "derzeit nicht berufstätig";

export const OPT_BERUFSSTATUS = [
  "angestellt", "Auszubildender", "Student", "selbstständig",
  "freiberuflich", "Beamter", OHNE_TAETIGKEIT, "sonstiges",
];

export const OPT_AV_ART = [
  "Private Rentenversicherung", "Riester-Rente", "Rürup-/Basisrente",
  "Betriebliche Altersvorsorge (bAV)", "Fondsgebundene Rentenversicherung",
  "Lebensversicherung", "Sonstige",
];

export const OPT_AK_ART = [
  "Berufsunfähigkeitsabsicherung", "Grundfähigkeitsabsicherung",
  "Unfallversicherung", "Sonstige Absicherung",
];

export const OPT_SACH = [
  "Privathaftpflicht", "Hausrat", "Rechtsschutz", "Kfz",
  "Tierhalterhaftpflicht", "Tier-OP-Versicherung", "Tierkrankenversicherung",
  "Wohngebäude", "Auslandskrankenversicherung",
  "Krankenzusatzversicherung", "Zahnzusatzversicherung", "Sonstige",
];
export const OPT_SACH_NONE = ["Keine", "Weiß ich nicht"];

export const OPT_VERMOEGEN = [
  "Girokonto", "Tagesgeld", "Sparbuch", "Bausparvertrag", "Depot", "Fonds",
  "Private Altersvorsorge", "Betriebliche Altersvorsorge", "Immobilie",
  "Bargeld", "Kryptowährungen", "Beteiligungen", "Sonstige Vermögenswerte",
];

// §14 Altersvergleich – Quelle laut PDF: Bundeszentrale für politische Bildung.
// ⚠ Vor Veröffentlichung auf Belastbarkeit prüfen (Brief §14). Bis dahin werden die
// konkreten Zahlen NICHT angezeigt (siehe SHOW_ALTERSVERGLEICH_WERTE). Erst nach
// Quellenprüfung auf true setzen.
export const SHOW_ALTERSVERGLEICH_WERTE = false;
export const ALTERSVERGLEICH: { range: string; wert: number }[] = [
  { range: "18 bis 20", wert: 6463 },
  { range: "21 bis 25", wert: 8934 },
  { range: "26 bis 30", wert: 23928 },
  { range: "31 bis 35", wert: 47489 },
  { range: "36 bis 40", wert: 84732 },
  { range: "41 bis 45", wert: 117533 },
  { range: "46 bis 50", wert: 145681 },
];

export const ADVISORS: { id: AdvisorId; label: string }[] = [
  { id: "nick", label: "Nick Frenzel" },
  { id: "aaron", label: "Aaron Magiera" },
  { id: "simon", label: "Simon Magiera" },
  { id: "unbekannt", label: "Ich weiß es gerade nicht" },
];

export const UPLOAD_KATEGORIEN = [
  "Einkommensnachweis", "Altersvorsorge", "Arbeitskraftabsicherung",
  "Sachversicherung", "Stromabrechnung", "Gasabrechnung",
  "Personalausweis Vorderseite", "Personalausweis Rückseite", "Sonstige relevante Unterlage",
];

export const ALLOWED_MIME = [
  "application/pdf", "image/jpeg", "image/jpg", "image/png", "image/heic", "image/heif",
];
export const ALLOWED_EXT = ["pdf", "jpg", "jpeg", "png", "heic", "heif"];
export const MAX_FILE_BYTES = 15 * 1024 * 1024;
